import {Component, OnDestroy, OnInit} from '@angular/core';
import {User} from '../../model/User';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthService} from '../../service/auth/auth.service';
import HttpStatusCode from '../../../global/util/HttpStatusCode';
import {InformType} from '../../../global/util/enum/InformType';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Constant} from '../../../global/util/Constant';
import {ApplicationService} from '../../service/application.service';
import {Dialog} from '../../../global/util/Dialog';
import {LocalStorage} from '../../../global/util/LocalStorage';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationCodeComponent} from '../../../public/components/confirmation-code/confirmation-code.component';
import {IdentificationDocument} from '../../../global/util/IdentificationDocument';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {
  public user = new User();
  public userForm: FormGroup;
  private returnUrl: string;
  public loading: boolean;
  public fieldTextType: boolean;
  public ROLE_SUPERADMIN = Constant.ROLE_SUPERADMIN;
  public ROLE_LITE_ADMIN = Constant.ROLE_LITE_ADMIN;
  public ADMIN_IOFE = Constant.ADMIN_IOFE;
  public ROLE_ADMIN_NOTCER = Constant.ROLE_ADMIN_NOTCER;
  public allyLoading = false;
  private alliancecode;
  private isLogin = true;

  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private authenticationService: AuthService,
              private modalService: NgbModal,
              private applicationService: ApplicationService,
              private activatedRoute: ActivatedRoute,
              private translate: TranslateService) {
    this.receiveParams();
    Dialog.createInstance(modalService);
    this.createForm();
    LocalStorage.setItem('onLoginPage', true);
  }

  ngOnDestroy(): void {
    LocalStorage.removeItem('onLoginPage');
  }

  public receiveParams() {
    this.activatedRoute.params.subscribe(params => {
      if (params.alliancecode) {
        this.allyLoading = true;
        this.alliancecode = params.alliancecode;
        this.applicationService.get(Constant.ROOT + '/login/' + this.alliancecode).subscribe(value => {
          this.allyLoading = false;
          localStorage.setItem('_allyconfiguration', JSON.stringify(value));
        }, () => {
          this.allyLoading = false;
          Dialog.show(InformType.Danger, this.translate.instant('TITLE.ALLY-DATA'), this.translate.instant('MESSAGE.UNABLE-LOAD-INFORMATION'));
        });
      }
    });
  }

  ngOnInit() {
    localStorage.clear();
    this.returnUrl = this.router.paramsInheritanceStrategy.anchor('returnUrl' || '/');
  }

  /* Método que permite verificar si el usuario que está intentando loguearse tiene doble factor de autenticación*/
  verifyTwoFactAuthAndLogin(user: User) {
    if (this.userForm.invalid) {
      Object.values(this.userForm.controls).forEach(control => {
        control.markAsTouched();
      });
      return;
    }
    const url = Constant.ROOT_API_V1.concat('/public/verify-two-factor-authentication?username=').concat(user.username);
    this.applicationService.get(url).subscribe((next: any) => {
      if (next.haveTwoFactorAuthentication) {
        this.getUUID();
        this.openModalTwoFactorAuth(next, user);
      } else {
        this.verifyHaveConfiguredChangePassword(user);
      }
    }, error => {
      this.loading = false;
      this.applicationService.setUserLoggedIn(false);
      if (error.status === HttpStatusCode.UNAUTHORIZED) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.ACCESS-PERMISSIONS-PROBLEMS'));
      } else if (error.status === HttpStatusCode.FORBIDDEN || error.status === HttpStatusCode.NOT_FOUND) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.CREDENTIAL-ARE-INCORRECT'));
      } else if (error.status === HttpStatusCode.LOCKED) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.YOUR-USER-IS-SUSPENDED-PLEASE-CONTACT-ADMINISTRATOR'));
      } else {
        Dialog.show(InformType.Danger, this.translate.instant('TITLE.LOGIN'));
      }
    });
  }

  login(user: User) {
    this.loading = true;
    this.authenticationService.login(user).subscribe(data => {
      this.loading = false;
      const onlyApi = data.headers.headers.get('onlyapi')[Constant.ZERO_INDEX];
      const active = data.headers.headers.get('active')[Constant.ZERO_INDEX];
      const deleted = data.headers.headers.get('deleted')[Constant.ZERO_INDEX];
      const userType = data.headers.headers.get('usertype')[Constant.ZERO_INDEX];
      const personId = data.headers.headers.get('personid')[Constant.ZERO_INDEX];
      console.log(personId);
      LocalStorage.setItem('userType', userType);
      if (Constant.ONLY_API_OPTION === onlyApi) {
        Dialog.show(InformType.Info, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.USER-NOT-HAVE-NECESSARY-PERMISSIONS'));
        return;
      } else if (Constant.INACTIVE === +active) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.PERSON-ASSOCIATED-IS-INACTIVE'));
        return;
      } else if (Constant.DELETED === +deleted) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.PERSON-ASSOCIATED-IS-DELETED'));
        return;
      }
      this.applicationService.setUserLoggedIn(true);
      this.loadStaticData();
    }, error => {
      this.loading = false;
      this.applicationService.setUserLoggedIn(false);
      if (error.status === HttpStatusCode.UNAUTHORIZED) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.ACCESS-PERMISSIONS-PROBLEMS'));
      } else if (error.status === HttpStatusCode.FORBIDDEN) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.CREDENTIAL-ARE-INCORRECT'));
      } else if (error.status === HttpStatusCode.LOCKED) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.YOUR-USER-IS-SUSPENDED-PLEASE-CONTACT-ADMINISTRATOR'));
      } else {
        Dialog.show(InformType.Danger, this.translate.instant('TITLE.LOGIN'));
      }
    });
  }

  private createForm() {
    this.userForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  public isInvalidControl(ctrlName: string) {
    return this.userForm.get(ctrlName).invalid && this.userForm.get(ctrlName).touched;
  }

  public hasRole(roleDescription: string) {
    return this.authenticationService.hasRole(roleDescription);
  }

  private loadStaticData() {
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/staticdata');
    this.applicationService.get(url).subscribe((value: any) => {
      this.loading = false;
      LocalStorage.setUpEmbeddedInfo(value);
      if (this.hasRole(this.ROLE_SUPERADMIN)) {
        this.router.navigate(['enterprise']);
      } else if (this.hasRole(this.ROLE_LITE_ADMIN) || this.hasRole(this.ROLE_ADMIN_NOTCER)) {
        this.router.navigate(['workflow']);
      }else if (this.hasRole(this.ADMIN_IOFE)) {
        this.router.navigate(['service-report']);
      } else {
        this.router.navigate(['document']);
      }
    }, () => {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.HOME'), this.translate.instant('MESSAGE.ERROR-LOADING-INITIAL-DATA'));
    });
  }

  public toggleFieldTextType(): void {
    this.fieldTextType = !this.fieldTextType;
  }

  openModalTwoFactorAuth(next: any, user?: any) {
    const modal = this.modalService.open(ConfirmationCodeComponent, {
      backdrop: 'static'
    });
    modal.componentInstance.value = this.getDocumentType(next).concat('_').concat(next.documentNumber);
    modal.componentInstance.email = next.email;
    modal.componentInstance.cellPhone = next.phoneNumber;
    modal.componentInstance.isLogin = true;
    modal.componentInstance.username = this.user.username;
    modal.componentInstance.title = this.translate.instant('TITLE.TWO-FACTOR-AUTHENTICATION');
    modal.componentInstance.messageInfo = this.translate.instant('MESSAGE.SEND-CODE-VERIFY-TWO-FACTOR-AUTHENTICATION');
    modal.result.then(isValidVerificationCode => {
      if (isValidVerificationCode) {
        this.verifyHaveConfiguredChangePassword(user);
      }
    });
  }

  /*Se obtiene el identificador unico universal*/
  private getUUID(): void {
    const url = Constant.ROOT_API_V1 + '/public/uuid';
    this.applicationService.get(url).subscribe(() => {
      // this.sendVerificationCode(next, value.uuid);
    }, () => {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.OBTAINING-UUID'));
    });
  }

  /**
   * Este método registra el código y luego envía al correo del usuario para su autenticación.
   */
  public sendVerificationCode(next: any, uuid?: string): void {
    const url = Constant.ROOT_API_V1 + '/public/send-verification-code';
    const formData = new FormData();
    formData.append('uuid', uuid);
    formData.append('value', this.getDocumentType(next).concat('_').concat(next.documentNumber));
    formData.append('email', next.email);
    formData.append('isLogin', String(this.isLogin));
    this.loading = true;
    this.applicationService.post(url, formData).subscribe(() => {
      this.loading = false;
    }, () => {
      this.loading = false;
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.VERIFICATION-CODE'), this.translate.instant('MESSAGE.ERROR-OCCURRED-TO-SEND-VERIFICATION-CODE'));
    });
  }

  getDocumentType(next: any): string {
    const documentType = Number(next.documentType);
    return documentType === Constant.DNI ? IdentificationDocument.dDNI : (documentType === Constant.CARNE_EXTRANJERIA ? IdentificationDocument.dCE : IdentificationDocument.dPAS);
  }

  verifyHaveConfiguredChangePassword(user: any) {
    const url = Constant.ROOT_API_V1.concat('/public/change/').concat(user.username).concat('/temporary-password');
    this.applicationService.get(url).subscribe((next: any) => {
      if (next.configuredPasswordChange === 'true') {
        this.verifyPasswordMatch(user, next.userId);
      } else {
        this.login(user);
      }
    });
  }

  private verifyPasswordMatch(user: any, userId?: any) {
    const url = Constant.ROOT_API_V1.concat('/public/verify-password-match/');
    const formData = new FormData();
    formData.append('username', user.username);
    formData.append('password', user.password);
    this.applicationService.post(url, formData).subscribe((next: any) => {
      console.log(next);
      if (next.passwordMatch === 'true') {
        this.router.navigate(['change-password'], {state: {username: user.username, userId}}).then();
      } else {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.CREDENTIAL-ARE-INCORRECT'));
      }
    }, error => {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.LOGIN'));
    });
  }
}
